/**
 * Les objectifs de performance d'une campagne, côté écran.
 *
 * ## Deux sens pour le mot « objectif »
 *
 * Celui-ci est **saisi dans Kokpit** : une cible que l'agence se donne. Il n'a rien à voir avec
 * l'`objective` de campagne des régies — `OUTCOME_SALES` chez Meta, `TRAFFIC` chez TikTok — qui
 * circule déjà sous ce nom dans les données de dashboard.
 *
 * Il n'a rien à voir non plus avec les **objectifs par métrique**
 * (`localStorage.kokpeet_metric_objectives`, lus par `getCpaClassName` / `getCosClassName` dans
 * `utils/metricsUtils.js`), qui colorent les cellules d'un tableau. Ceux-là sont locaux au
 * navigateur ; ceux-ci vivent en base, sont partagés par toute l'organisation, et alimentent les
 * outils d'enchères. Les fusionner serait une erreur.
 */

/** Tous les types, dans l'ordre d'affichage. Doit refléter l'enum `ObjectiveType` de Prisma. */
export const TYPES_OBJECTIF = ['CPA', 'ROAS', 'CPC', 'CONVERSIONS'];

/**
 * Les seuls types que l'Optimiseur d'enchères et les Ajustements par appareil savent lire.
 *
 * Les deux autres sont **déclaratifs** : on les affiche, on les compare au réalisé, mais aucun
 * moteur ne s'en sert. L'écran doit le dire, sinon quelqu'un posera un CPC en croyant piloter ses
 * enchères.
 */
export const TYPES_OBJECTIF_ENCHERES = ['CPA', 'ROAS'];

/**
 * Libellé, unité et pas de saisie de chaque type.
 *
 * ⚠️ `ROAS` se saisit et se stocke **en pourcentage** : 400 pour 400 %, et non 4. C'est la forme
 * historique en base ; la changer ferait dériver silencieusement tous les objectifs déjà saisis.
 * Sans l'unité affichée à côté du champ, personne ne devine laquelle des deux écritures attendre.
 */
export const DEFINITIONS_OBJECTIF = {
  CPA: { libelle: 'CPA cible', unite: '€', pas: '0.01', aide: 'Coût par acquisition visé' },
  ROAS: { libelle: 'ROAS cible', unite: '%', pas: '1', aide: 'Retour sur dépense visé, en pourcentage (400 = 400 %)' },
  CPC: { libelle: 'CPC cible', unite: '€', pas: '0.01', aide: 'Coût par clic visé' },
  CONVERSIONS: { libelle: 'Conversions', unite: '', pas: '1', aide: 'Volume de conversions visé sur la période' }
};

/** Un type est-il utilisé par les outils d'enchères ? */
export function estActionnable(objectiveType) {
  return TYPES_OBJECTIF_ENCHERES.includes(objectiveType);
}

/** Affichage court d'un objectif : « CPA 12,00 € », « ROAS 400 % », « 150 conversions ». */
export function formaterObjectif(objectif) {
  if (!objectif) return '—';
  const def = DEFINITIONS_OBJECTIF[objectif.objectiveType];
  const valeur = Number(objectif.objectiveValue);

  if (!def) return `${objectif.objectiveType} ${valeur}`;
  if (objectif.objectiveType === 'CONVERSIONS') {
    return `${valeur.toLocaleString('fr-FR')} conversions`;
  }
  const decimales = def.unite === '€' ? 2 : 0;
  return `${def.libelle.replace(' cible', '')} ${valeur.toLocaleString('fr-FR', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales
  })} ${def.unite}`.trim();
}

/**
 * Normalise la réponse des objectifs en `{ [campaignId]: Objectif[] }`.
 *
 * ## Pourquoi tolérer deux formes
 *
 * `GET /api/tools/campaign-objectives` rendait une **map d'objets** (un objectif par campagne) et
 * rend désormais une **map de listes**. Or le frontend et le backend ne se déploient pas ensemble :
 * Vercel publie en une minute là où Railway rebâtit, migre et pousse le schéma en plusieurs. Il
 * existe donc une fenêtre pendant laquelle le nouvel écran interroge l'ancienne API — et la
 * symétrique, si un retour arrière est décidé.
 *
 * Accepter les deux formes coûte dix lignes et supprime la fenêtre. Ce n'est pas la même chose
 * qu'une clé de compatibilité côté serveur, qui devrait *choisir* un objectif quand deux existent :
 * tout choix y serait faux, et c'est exactement le piège qu'on vient de retirer du code.
 */
export function normaliserObjectifs(brut) {
  if (!brut || typeof brut !== 'object') return {};

  const parCampagne = {};
  for (const [campaignId, valeur] of Object.entries(brut)) {
    if (!valeur) continue;
    const liste = Array.isArray(valeur) ? valeur : [valeur];
    parCampagne[campaignId] = liste.filter(o => o && o.objectiveType);
  }
  return parCampagne;
}

/** L'objectif d'un type donné dans une liste, ou `undefined`. Jamais de repli sur un autre type. */
export function objectifDuType(objectifs, objectiveType) {
  return (objectifs || []).find(o => o.objectiveType === objectiveType);
}

/**
 * L'objectif qu'un outil d'enchères doit retenir pour une campagne, ou `undefined`.
 *
 * Deux règles en une :
 *
 * 1. **Seuls le CPA et le ROAS sont retenus.** Un CPC ou un volume de conversions n'est pas
 *    renvoyé — et surtout jamais converti en autre chose. Le code d'origine faisait
 *    `if (type === 'CPA') … else …`, si bien qu'un CPC de 0,45 € se serait affiché « ROAS 0 % » et
 *    aurait écrasé la cible de rentabilité du moteur.
 * 2. **Le CPA l'emporte sur le ROAS**, priorité fixe, identique à celle du serveur
 *    (`backend/src/lib/objectifsPourEncheres.js`). Une règle fixe s'explique ; départager par date
 *    de modification donnerait un résultat exact mais que rien à l'écran ne justifierait.
 */
export function objectifActionnable(objectifs) {
  return TYPES_OBJECTIF_ENCHERES
    .map(type => objectifDuType(objectifs, type))
    .find(Boolean);
}
